import "./Legal.scss";
import { Link } from "react-router-dom";
import Nav from "../Nav/Nav";

const PrivacyPolicy = () => {
  return (
    <>
      <div className="legal-header">
        <Nav />
      </div>
      <div className="legal-page">
        <div className="legal-breadcrumb">
          <Link to="/">← Home</Link>
        </div>
        <article className="legal-content">
          <header className="legal-intro">
            <h1>Bayun Privacy Policy</h1>
            <p className="effective-date">Effective date: September 9, 2026</p>
            <p>
              This Privacy Policy explains how Bayun Systems, Inc. ("we," "us,"
              "Bayun") collects and uses information in connection with the Bayun
              Developer Portal, SDKs, and hosted service (together, the
              "Service"), available at{" "}
              <a href="https://bayun.dev" target="_blank" rel="noopener noreferrer">
                bayun.dev
              </a>
              .
            </p>
          </header>

          <section>
            <h2>1. Our principle</h2>
            <p>
              We collect only the information that is{" "}
              <strong>strictly necessary to provide the Service</strong>, and we
              use it <strong>only for that purpose</strong>. We do not sell your
              data or your end users' data.
            </p>
          </section>

          <section>
            <h2>2. Information we collect</h2>
            <p>
              <strong>From you (the Developer):</strong>
            </p>
            <ul>
              <li>
                Account information needed to register and operate your Developer
                Portal account (e.g., name, email, organization, authentication
                credentials, and, if applicable, billing details for commercial
                agreements).
              </li>
              <li>
                Technical information needed to operate the Service for your
                applications (e.g., API keys, application identifiers and
                secrets, and usage/telemetry data such as request volume, used to
                operate the Service, enforce fair-usage limits, and maintain
                reliability).
              </li>
              <li>
                Additional information about you and your application's use case,
                which we may request to continue providing Service or reinstate
                after suspension (see our{" "}
                <Link to="/bayun-terms-of-service">Terms of Service</Link>).
              </li>
              <li>
                Any other information you may provide when contacting us or
                requesting commercial services
              </li>
            </ul>
            <p>
              <strong>From your applications' end users (via the SDKs):</strong>
            </p>
            <p>
              Bayun's SDKs are designed to protect your end users' data (e.g.,
              through encryption and related security functions). We process only
              the technical data necessary for the SDK and hosted Service to
              perform that function. We do not access or use your end users'
              underlying data for any purpose beyond providing the Service to
              you. You are responsible for determining what user data your
              applications send to Bayun, and for providing any notices or
              obtaining any consents required by applicable law
            </p>
            <p>
              We do not knowingly collect more than what is needed to
              authenticate accounts, deliver the SDK/hosted functionality, keep
              the Service secure, and manage the limited free-tier throttling
              described in our{" "}
              <Link to="/bayun-terms-of-service">Terms of Service</Link>.
            </p>
          </section>

          <section>
            <h2>3. How we use information</h2>
            <p>We use the information we collect only to:</p>
            <ul>
              <li>
                Provide, operate, and maintain the Service (including the free
                hosted service and SDKs).
              </li>
              <li>
                Monitor system load and usage to manage throttling or limits as
                described in our{" "}
                <Link to="/bayun-terms-of-service">Terms of Service</Link>.
              </li>
              <li>
                Review accounts for potential abuse or spam and determine
                eligibility for service reinstatement.
              </li>
              <li>
                Provide support and communicate with you about your account or
                the Service.
              </li>
              <li>
                Support commercial agreements (SLA, self-hosted binaries, or
                cloud marketplace arrangements) if you enter into one.
              </li>
              <li>Comply with legal obligations.</li>
            </ul>
            <p>
              We do <strong>not</strong> sell your data or use it for
              advertising.
            </p>
          </section>

          <section>
            <h2>4. Data sharing</h2>
            <p>
              We do not share developer or end-user information with third
              parties, except:
            </p>
            <ul>
              <li>
                Where necessary to operate the Service (e.g., infrastructure
                providers who process data on our behalf, under confidentiality
                obligations).
              </li>
              <li>
                If you use a cloud marketplace commercial agreement, the relevant
                cloud provider (AWS, GCP, Azure, etc.) as needed to facilitate
                that arrangement.
              </li>
              <li>Where required by law.</li>
            </ul>
          </section>

          <section>
            <h2>5. Self-hosted deployments</h2>
            <p>
              If you obtain server binaries under a commercial agreement to
              self-host the Service, data processed by your self-hosted instance
              is controlled by you, not Bayun. This Privacy Policy applies to
              data collected through the Bayun-hosted Service and Developer
              Portal.
            </p>
          </section>

          <section>
            <h2>6. Data retention</h2>
            <p>
              We retain information only as long as necessary to provide the
              Service or comply with legal obligations. If your account is
              terminated, we will delete or anonymize your data within a
              reasonable period, unless retention is required by law.
            </p>
          </section>

          <section>
            <h2>7. Your rights</h2>
            <p>
              You may request access to, correction of, or deletion of your
              developer account information at any time by contacting us.
            </p>
          </section>

          <section>
            <h2>8. Changes to this policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will make
              reasonable efforts to communicate material changes. Continued use
              of the Service after changes take effect means you accept the
              updated policy.
            </p>
          </section>

          <section>
            <h2>9. Contact</h2>
            <p>
              Questions about this Privacy Policy? Contact us through{" "}
              <a href="https://bayun.dev" target="_blank" rel="noopener noreferrer">
                Bayun.dev
              </a>
              .
            </p>
          </section>
        </article>
      </div>
    </>
  );
};

export default PrivacyPolicy;
